import { useEffect, useMemo, useState } from "react";

import { CheckCircleOutlined, Delete, ErrorOutlineOutlined } from "@material-ui/icons";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import { Button, Modal, Select } from "antd";
import { AxiosResponse } from "axios";
import { ArpsDesignerKind } from "constants/forecasts.constants";
import getColumnLabelWithContext from "utils/column/getColumnLabelWithContext";

import { ForecastActivityType, TYPE_WELLS } from "../../../../constants";
import useBetaFeatures from "../../../../hooks/useBetaFeatures";
import useColumns from "../../../../hooks/useColumns";
import { UserArpsFile } from "../../../../models/UserArpsModel";
import { EntityKind } from "../../../../models/entityKind";
import { IGroupByMap } from "../../../../models/groupBy";
import { isPrimaryProduct } from "../../../../utils/arps/productHelpers";
import { BusyIndicator } from "../../../activity/shared";
import { forecastArrayProductOptions } from "../../../arps/widget/TypeWellEditor/tabs/ForecastArrayProductOptions";
import {
  TypeWellProductItem,
  typeWellProductsMenuItems
} from "../../../arps/widget/TypeWellEditor/tabs/TypeWellProductOptions";
import { ErrorBoundary, Tooltip } from "../../../base";
import GroupBy from "../../../groupBy";
import { IconSpinner } from "../../../icons";
import { UseArpsUploadWizardReturn, useSelectedProject } from "../../projects/hooks";
import { useArpsUploadMutations } from "../mutations/useArpsUploadMutations";
import {
  ActionButton,
  ContentContainer,
  DeleteButtonWrapper,
  ErrorInstructions,
  ErrorMessage,
  ErrorWrapper,
  Instructions,
  MatchedWrapper,
  NoProductsFound,
  PrimaryColumnWrapper,
  PrimaryProductButton,
  ProductDropdownWrapper,
  ProductMappingContainer,
  ProductMappingHeader,
  ProductMappingRow,
  ProductMappingRowTitle,
  ProductName,
  ProductsContainer,
  RootContainer,
  StyledMappingButton,
  Title,
  WarningWrapper
} from "./ArpsUploadWizard.styles";
import {
  findBestProductMatch,
  findBestWellDataColumnMatch,
  getDuplicateMappings,
  getProductOptions,
  normalizeGroupByProperty
} from "./ArpsUploadWizardHelpers";
import { UPLOAD_FILE_TYPES, UploadFileType, WellDataColumnMapping } from "./constants";

interface ArpsUploadWizardProps {
  type: ForecastActivityType;
  onUploadSuccess: (response: AxiosResponse, requestData: Record<string, string>) => void;
  onLoadingStateChange: (isLoading: boolean) => void;
  onUploadError: (error: unknown) => void;
  arpsUploadWizard: UseArpsUploadWizardReturn;
  projectId?: string; // Optional override for organization forecasts
}

const ArpsUploadWizard = ({
  type,
  onUploadSuccess,
  onLoadingStateChange,
  onUploadError,
  arpsUploadWizard,
  projectId: customProjectId
}: ArpsUploadWizardProps) => {
  const {
    selectedFolderId,
    selectedFolderName,
    files,
    closeWizard,
    isWizardOpen,
    initialError,
    initialErrorInstructions
  } = arpsUploadWizard;

  const { hasFeature } = useBetaFeatures();
  const calculatedWidth = Math.max(window.innerWidth * 0.3, 610);
  const [modalWidth, setModalWidth] = useState(calculatedWidth);
  const [detectedProducts, setDetectedProducts] = useState<string[]>([]);
  const [deletedProducts, setDeletedProducts] = useState<string[]>([]);
  const [sortedProducts, setSortedProducts] = useState<string[]>([]);
  const [dateWarnings, setDateWarnings] = useState<string[]>([]);
  const [productMappings, setProductMappings] = useState<Record<string, string>>({});
  const [nonExactMatches, setNonExactMatches] = useState<Set<string>>(new Set());
  const [uploadFileType, setUploadFileType] = useState<UploadFileType | null>(null);
  const [primaryProduct, setPrimaryProduct] = useState<string>("");
  const [defaultPrimaryProduct, setDefaultPrimaryProduct] = useState<string>("");
  const [loadingError, setLoadingError] = useState(null);
  const { selectedProject: project } = useSelectedProject();
  const projectId = customProjectId || project?.projectId;

  // Well data upload specific state
  const [detectedHeaders, setDetectedHeaders] = useState<string[]>([]);
  const [deletedHeaders, setDeletedHeaders] = useState<string[]>([]);
  const [sortedHeaders, setSortedHeaders] = useState<string[]>([]);
  const [wellDataMappings, setWellDataMappings] = useState<Record<string, string | null>>(
    {}
  );
  const emptyGroupByValue = useMemo<IGroupByMap>(() => ({ property: "", title: "" }), []);

  // Get all available columns for well data mapping (numeric fields only)
  const allColumns = useColumns();
  const numericColumns = useMemo(
    () =>
      allColumns.filter((col) => col.dataType === "Number" || col.dataType === "Integer"),
    [allColumns]
  );

  // Check if this is a well data upload
  const isWellDataUpload = uploadFileType === UPLOAD_FILE_TYPES.WELL_DATA_UPLOAD;

  const hasForecastLiquidProducts = hasFeature("Forecast Liquid Products");

  const [supportedProducts] = useState<TypeWellProductItem[]>(
    typeWellProductsMenuItems(
      ArpsDesignerKind.TypeWell,
      hasForecastLiquidProducts
    ).filter((item) => item.type !== "divider")
  );

  const resetState = () => {
    setDetectedProducts(null);
    setLoadingError(null);
    setProductMappings({});
    setDeletedProducts([]);
    setSortedProducts([]);
    setDateWarnings([]);
    setNonExactMatches(new Set());
    setUploadFileType(null);
    setPrimaryProduct("");
    setDefaultPrimaryProduct("");
    // Reset well data upload state
    setDetectedHeaders([]);
    setDeletedHeaders([]);
    setSortedHeaders([]);
    setWellDataMappings({});
    closeWizard && closeWizard();
  };

  // Convert raw File objects to UserArpsFile objects
  const processedFiles = files
    ? files.map((file) => {
        const userArps = new UserArpsFile();
        userArps.file = file.name;
        userArps.fileUpload = file;
        userArps.source = "valnav";
        userArps.fileType = "upload";
        return userArps;
      })
    : [];

  const duplicateMappings = getDuplicateMappings(productMappings);

  // Check for duplicate well data mappings (mapping multiple columns to the same field)
  const duplicateWellDataMappings = getDuplicateMappings(
    Object.fromEntries(
      Object.entries(wellDataMappings).filter(([, property]) => property !== null) as [
        string,
        string
      ][]
    )
  );

  const { detectProductsMutation, uploadArpsMutation, uploadWellDataMutation } =
    useArpsUploadMutations({
      projectId,
      selectedFolderId,
      selectedFolderName,
      type,
      processedFiles,
      onLoadingStateChange,
      onUploadSuccess,
      onUploadError,
      resetState
    });

  // Detect products when the wizard opens
  useEffect(() => {
    if (isWizardOpen && selectedFolderId && files && files.length > 0 && !initialError) {
      detectProductsMutation.mutate();
    }
  }, [isWizardOpen, selectedFolderId, files, initialError]);

  useEffect(() => {
    if (isWizardOpen && initialError) {
      setLoadingError(initialError);
      setDetectedProducts(null);
      setDetectedHeaders([]);
    }
  }, [isWizardOpen, initialError]);

  // Update the modal width based on the window size
  useEffect(() => {
    const handleResize = () => {
      const calculatedWidth = Math.max(window.innerWidth * 0.3, 610);
      setModalWidth(calculatedWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Automatically map detected products to supported products
  useEffect(() => {
    if (detectedProducts && detectedProducts.length > 0) {
      const availableOptions =
        uploadFileType === UPLOAD_FILE_TYPES.FORECAST_ARRAY
          ? forecastArrayProductOptions
          : supportedProducts;

      const automaticMappings = { ...productMappings };
      const newNonExactMatches = new Set<string>();

      detectedProducts.forEach((product: string) => {
        if (!automaticMappings[product]) {
          const matchResult = findBestProductMatch(product, availableOptions);

          if (matchResult.key) {
            automaticMappings[product] = matchResult.key;

            // Only mark as a non-exact match if it wasn't an exact match
            if (!matchResult.isExactMatch) {
              newNonExactMatches.add(product);
            }
          }
        }
      });

      const initialSortedProducts = [...detectedProducts].sort((a, b) => {
        if (!automaticMappings[a] && automaticMappings[b]) return -1;
        if (automaticMappings[a] && !automaticMappings[b]) return 1;
        // Otherwise, alphabetical order
        return a.localeCompare(b);
      });

      setProductMappings(automaticMappings);
      setNonExactMatches(newNonExactMatches);
      setSortedProducts(initialSortedProducts);

      // Initialize primary product to the mapped value of defaultPrimaryProduct (for type wells)
      // Only set if it's a valid primary product (not a ratio like GOR, CGR, etc.)
      if (
        type === TYPE_WELLS &&
        defaultPrimaryProduct &&
        automaticMappings[defaultPrimaryProduct] &&
        isPrimaryProduct(automaticMappings[defaultPrimaryProduct])
      ) {
        setPrimaryProduct(automaticMappings[defaultPrimaryProduct]);
      }
    }
  }, [
    detectedProducts,
    uploadFileType,
    supportedProducts,
    forecastArrayProductOptions,
    type,
    defaultPrimaryProduct
  ]);

  // Update the detected products and loading error state based on the mutation result
  useEffect(() => {
    if (detectProductsMutation.isSuccess && detectProductsMutation.data) {
      const fileType = detectProductsMutation.data.data?.fileType;
      setUploadFileType(fileType);

      if (fileType === UPLOAD_FILE_TYPES.WELL_DATA_UPLOAD) {
        // Well data upload - the backend returns column headers in the "products" field
        setDetectedHeaders(detectProductsMutation.data.data?.products || []);
        setDetectedProducts(null);
      } else {
        // Regular product upload
        setDetectedProducts(detectProductsMutation.data.data?.products);
        setDateWarnings(detectProductsMutation.data.data?.warnings);
        setDetectedHeaders([]);
        // Capture default primary product for type well uploads (will be used after mappings are set)
        const detectedDefault =
          detectProductsMutation.data.data?.defaultPrimaryProduct || "";
        setDefaultPrimaryProduct(detectedDefault);
      }
      setLoadingError(null);
    } else if (detectProductsMutation.isError) {
      const axiosError = detectProductsMutation.error as {
        response?: { data?: { error?: string } };
      };
      setLoadingError(axiosError.response?.data?.error);
      setDetectedProducts(null);
      setDetectedHeaders([]);
    }
  }, [
    detectProductsMutation.status,
    detectProductsMutation.data,
    detectProductsMutation.error
  ]);

  // Auto-map well data headers when detected
  useEffect(() => {
    if (isWellDataUpload && detectedHeaders && detectedHeaders.length > 0) {
      const automaticMappings: Record<string, string | null> = {};
      const newNonExactMatches = new Set<string>();

      detectedHeaders.forEach((header: string) => {
        const matchResult = findBestWellDataColumnMatch(header, numericColumns);

        if (matchResult.column) {
          automaticMappings[header] = matchResult.column.property;
          if (!matchResult.isExactMatch) {
            newNonExactMatches.add(header);
          }
        } else {
          automaticMappings[header] = null;
        }
      });

      // Sort: unmapped first, then non-exact matches, then exact matches
      const sortedList = [...detectedHeaders].sort((a, b) => {
        if (!automaticMappings[a] && automaticMappings[b]) return -1;
        if (automaticMappings[a] && !automaticMappings[b]) return 1;
        if (newNonExactMatches.has(a) && !newNonExactMatches.has(b)) return -1;
        if (!newNonExactMatches.has(a) && newNonExactMatches.has(b)) return 1;
        return a.localeCompare(b);
      });

      setWellDataMappings(automaticMappings);
      setNonExactMatches(newNonExactMatches);
      setSortedHeaders(sortedList);
    }
  }, [isWellDataUpload, detectedHeaders, numericColumns]);

  const handleClose = () => {
    resetState();
  };

  const handleSubmit = () => {
    if (isWellDataUpload) {
      const mappings: WellDataColumnMapping[] = Object.entries(wellDataMappings)
        .filter(
          ([header, property]) => property !== null && !deletedHeaders.includes(header)
        )
        .map(([sourceColumn, property]) => ({
          sourceColumn,
          targetField: property as string
        }));

      uploadWellDataMutation.mutate({ mappings });
    } else {
      uploadArpsMutation.mutate({
        productMappings,
        deletedProducts,
        primaryProduct: type === TYPE_WELLS ? primaryProduct : undefined
      });
    }
    resetState();
  };

  const handleProductMappingChange = (product: string, mappedTo: string) => {
    setProductMappings((prev) => ({
      ...prev,
      [product]: mappedTo
    }));
  };

  // Well data mapping change handler
  const handleWellDataMappingChange = (header: string, property: string) => {
    setWellDataMappings((prev) => ({
      ...prev,
      [header]: property
    }));
    // Clear non-exact match status when user manually selects
    if (nonExactMatches.has(header)) {
      const updatedMatches = new Set(nonExactMatches);
      updatedMatches.delete(header);
      setNonExactMatches(updatedMatches);
    }
  };

  const allProductsMapped = () => {
    if (!detectedProducts || detectedProducts.length === 0) return false;

    // Check if all non-deleted products have mappings
    return detectedProducts
      .filter((product: string) => !deletedProducts.includes(product))
      .every((product: string) => !!productMappings[product]);
  };

  // Check if all well data headers are mapped
  const allWellDataHeadersMapped = () => {
    if (!detectedHeaders || detectedHeaders.length === 0) return false;

    // Check if all non-deleted headers have mappings
    return detectedHeaders
      .filter((header: string) => !deletedHeaders.includes(header))
      .every((header: string) => wellDataMappings[header] !== null);
  };

  const handleDeleteProduct = (product: string) => {
    const mappedValue = productMappings[product];
    if (mappedValue) {
      const newMappings = { ...productMappings };
      delete newMappings[product];
      setProductMappings(newMappings);
    }

    const updatedDeletedProducts = [...deletedProducts, product];
    setDeletedProducts(updatedDeletedProducts);

    // If deleted product was the primary, reset to another available valid primary product
    if (mappedValue === primaryProduct) {
      const remainingProducts = detectedProducts?.filter(
        (p: string) => !updatedDeletedProducts.includes(p)
      );
      // Find the first remaining product that can be a primary (not a ratio)
      const newPrimaryProduct = remainingProducts?.find(
        (p: string) => productMappings[p] && isPrimaryProduct(productMappings[p])
      );
      if (newPrimaryProduct && productMappings[newPrimaryProduct]) {
        setPrimaryProduct(productMappings[newPrimaryProduct]);
      } else {
        setPrimaryProduct("");
      }
    }

    if (detectedProducts && updatedDeletedProducts.length === detectedProducts.length) {
      // If all products are deleted, close the modal
      resetState();
    }
  };

  // Delete handler for well data headers
  const handleDeleteHeader = (header: string) => {
    if (wellDataMappings[header]) {
      const newMappings = { ...wellDataMappings };
      delete newMappings[header];
      setWellDataMappings(newMappings);
    }

    const updatedDeletedHeaders = [...deletedHeaders, header];
    setDeletedHeaders(updatedDeletedHeaders);

    if (detectedHeaders && updatedDeletedHeaders.length === detectedHeaders.length) {
      // If all headers are deleted, close the modal
      resetState();
    }
  };

  // Markup helper functions
  const renderFooter = () => {
    const isSubmitDisabled = isWellDataUpload
      ? detectProductsMutation.isLoading ||
        uploadWellDataMutation.isLoading ||
        loadingError !== null ||
        !allWellDataHeadersMapped()
      : detectProductsMutation.isLoading ||
        uploadArpsMutation.isLoading ||
        loadingError !== null ||
        !allProductsMapped();

    return [
      <Button key="cancel" onClick={handleClose}>
        Cancel
      </Button>,
      <Button
        key="submit"
        type="primary"
        onClick={handleSubmit}
        disabled={isSubmitDisabled}>
        Submit
      </Button>
    ];
  };

  const renderDuplicateMappingsWarning = () => {
    const hasDuplicates = isWellDataUpload
      ? duplicateWellDataMappings.length > 0
      : duplicateMappings.length > 0;

    if (!hasDuplicates) return null;

    return (
      <ErrorWrapper>
        <ErrorOutlineOutlined />
        <div>
          {isWellDataUpload
            ? "You have mapped more than one column to the same field"
            : "You have mapped more than one column to the same product"}
        </div>
      </ErrorWrapper>
    );
  };

  const renderDateWarning = () => {
    const count = dateWarnings.length;
    const message =
      count === 1
        ? "1 date has an invalid format and must be corrected"
        : `${count} dates have invalid formats and must be corrected`;

    return (
      <ErrorMessage>
        {message}
        <ErrorInstructions>
          Please ensure all date columns are formatted as date cells in Excel and
          re-upload your file.
        </ErrorInstructions>
      </ErrorMessage>
    );
  };

  const renderLoadingState = () => (
    <BusyIndicator>
      <IconSpinner />
    </BusyIndicator>
  );

  const renderErrorState = () => (
    <ErrorMessage>
      {loadingError}
      <ErrorInstructions>
        {initialErrorInstructions ??
          "Please check your file format and ensure it meets the required specifications. Fix any issues in the file and try uploading again."}
      </ErrorInstructions>
    </ErrorMessage>
  );

  const renderMappingInstructions = () => (
    <Title>
      Map detected products to our supported product types: <br /> <br />
      <Instructions>
        You can delete any unwanted products. All products must be mapped to proceed.
      </Instructions>
    </Title>
  );

  const renderWellDataMappingInstructions = () => (
    <Title>
      Map detected columns to well data fields: <br /> <br />
      <Instructions>
        You can delete any unwanted columns. All columns must be mapped to proceed.
      </Instructions>
    </Title>
  );

  const getStatusIcon = (isUnmapped: boolean, isNonExactMatch: boolean) => {
    if (isUnmapped) {
      return (
        <ErrorWrapper>
          <ErrorOutlineOutlined />
        </ErrorWrapper>
      );
    }
    if (isNonExactMatch) {
      return (
        <WarningWrapper>
          <ErrorOutlineOutlined />
        </WarningWrapper>
      );
    }
    return (
      <MatchedWrapper>
        <CheckCircleOutlined />
      </MatchedWrapper>
    );
  };

  const renderProductMappingRow = (product: string) => {
    const isUnmapped = !productMappings[product];
    const isNonExactMatch = nonExactMatches.has(product);
    // Only show Primary column for type wells with decline files (ValnavDecline, MosaicDecline, HarmonyDecline)
    const showPrimaryColumn =
      type === TYPE_WELLS &&
      (uploadFileType === UPLOAD_FILE_TYPES.VALNAV_DECLINE ||
        uploadFileType === UPLOAD_FILE_TYPES.MOSAIC_DECLINE ||
        uploadFileType === UPLOAD_FILE_TYPES.HARMONY_DECLINE);
    const mappedValue = productMappings[product];
    const isPrimary = mappedValue && mappedValue === primaryProduct;
    // Only allow setting primary for non-ratio products (Oil, Gas, Condensate, Water)
    const canBePrimary = mappedValue && isPrimaryProduct(mappedValue);

    const handlePrimaryClick = () => {
      if (canBePrimary && !isPrimary) {
        setPrimaryProduct(mappedValue);
      }
    };

    return (
      <ProductMappingRow key={product} showPrimary={showPrimaryColumn}>
        {showPrimaryColumn && (
          <PrimaryColumnWrapper>
            {canBePrimary && (
              <Tooltip title={isPrimary ? "Primary product" : "Set as primary product"}>
                <PrimaryProductButton
                  isPrimary={!!isPrimary}
                  onClick={handlePrimaryClick}
                  disabled={!mappedValue || isPrimary}>
                  P
                </PrimaryProductButton>
              </Tooltip>
            )}
          </PrimaryColumnWrapper>
        )}
        <ProductMappingRowTitle>
          {getStatusIcon(isUnmapped, isNonExactMatch)}
          <ProductName>{product}</ProductName>
        </ProductMappingRowTitle>
        <ProductDropdownWrapper>
          <Select
            placeholder="Select product type"
            value={productMappings[product] || undefined}
            onChange={(value) => {
              handleProductMappingChange(product, value);
              if (nonExactMatches.has(product)) {
                const updatedMatches = new Set(nonExactMatches);
                updatedMatches.delete(product);
                setNonExactMatches(updatedMatches);
              }
              // If this was the primary product and the mapping changed to another valid primary product
              if (showPrimaryColumn && isPrimary && isPrimaryProduct(value)) {
                setPrimaryProduct(value);
              } else if (showPrimaryColumn && isPrimary && !isPrimaryProduct(value)) {
                // If changed to a ratio, clear the primary product selection
                setPrimaryProduct("");
              }
            }}
            style={{ width: "100%" }}>
            {getProductOptions(uploadFileType, supportedProducts)}
          </Select>
        </ProductDropdownWrapper>
        <DeleteButtonWrapper>
          <Tooltip title="Delete Product">
            <ActionButton
              danger
              icon={<Delete />}
              onClick={() => handleDeleteProduct(product)}
            />
          </Tooltip>
        </DeleteButtonWrapper>
      </ProductMappingRow>
    );
  };

  // Render well data mapping row with GroupBy selector
  const renderWellDataMappingRow = (header: string) => {
    const mappedProperty = wellDataMappings[header];
    const isUnmapped = mappedProperty === null || mappedProperty === undefined;
    const isNonExactMatch = nonExactMatches.has(header);

    // Find the column for the mapped property to get display info
    const normalizedMappedProperty = normalizeGroupByProperty(mappedProperty);
    const mappedColumn = normalizedMappedProperty
      ? numericColumns.find((col) => col.property === normalizedMappedProperty)
      : null;

    // Get the display title for the selected column
    const displayTitle = mappedColumn
      ? getColumnLabelWithContext(mappedColumn, false)?.fullContextTitle ||
        mappedColumn.title
      : "Select Field";

    const groupByValue = mappedColumn ?? emptyGroupByValue;

    return (
      <ProductMappingRow key={header}>
        <ProductMappingRowTitle>
          {getStatusIcon(isUnmapped, isNonExactMatch)}
          <ProductName>{header}</ProductName>
        </ProductMappingRowTitle>
        <ProductDropdownWrapper>
          <GroupBy
            value={groupByValue}
            dataTypeFilters={["Number", "Integer"]}
            entityKind={EntityKind.Well}
            placement="rightTop"
            onChange={(selectedGroupBy) => {
              const normalizedProperty = normalizeGroupByProperty(
                selectedGroupBy?.property
              );
              handleWellDataMappingChange(header, normalizedProperty);
            }}>
            <StyledMappingButton block>
              <span>{displayTitle}</span>
              <KeyboardArrowRightIcon style={{ fontSize: 20 }} />
            </StyledMappingButton>
          </GroupBy>
        </ProductDropdownWrapper>
        <DeleteButtonWrapper>
          <Tooltip title="Delete Column">
            <ActionButton
              danger
              icon={<Delete />}
              onClick={() => handleDeleteHeader(header)}
            />
          </Tooltip>
        </DeleteButtonWrapper>
      </ProductMappingRow>
    );
  };

  const renderProductMappingSection = () => {
    if (!detectedProducts || detectedProducts.length === 0) {
      return (
        <NoProductsFound>No products detected in the uploaded files.</NoProductsFound>
      );
    }

    const filteredProducts = sortedProducts.filter(
      (product: string) => !deletedProducts.includes(product)
    );

    // Only show Primary column for type wells with decline files (ValnavDecline, MosaicDecline, HarmonyDecline)
    const showPrimaryColumn =
      type === TYPE_WELLS &&
      (uploadFileType === UPLOAD_FILE_TYPES.VALNAV_DECLINE ||
        uploadFileType === UPLOAD_FILE_TYPES.MOSAIC_DECLINE ||
        uploadFileType === UPLOAD_FILE_TYPES.HARMONY_DECLINE);

    return (
      <ProductMappingContainer>
        <ProductMappingHeader showPrimary={showPrimaryColumn}>
          {showPrimaryColumn && <div>Primary</div>}
          <div>Detected product</div>
          <div>Map to</div>
        </ProductMappingHeader>
        {filteredProducts.map(renderProductMappingRow)}
      </ProductMappingContainer>
    );
  };

  // Render well data mapping section
  const renderWellDataMappingSection = () => {
    if (!detectedHeaders || detectedHeaders.length === 0) {
      return <NoProductsFound>No columns detected in the uploaded file.</NoProductsFound>;
    }

    const filteredHeaders = sortedHeaders.filter(
      (header: string) => !deletedHeaders.includes(header)
    );

    return (
      <ProductMappingContainer>
        <ProductMappingHeader>
          <div>Detected column</div>
          <div>Map to field</div>
        </ProductMappingHeader>
        {filteredHeaders.map(renderWellDataMappingRow)}
      </ProductMappingContainer>
    );
  };

  // Determine modal title
  const getModalTitle = () => {
    if (isWellDataUpload) {
      return "Well Data Upload";
    }
    return `${type === TYPE_WELLS ? "Type Well" : "Forecast"} Upload`;
  };

  // Check if we have data to display (products or headers depending on upload type)
  const hasDataToDisplay = isWellDataUpload
    ? detectedHeaders && detectedHeaders.length > 0
    : detectedProducts && detectedProducts.length > 0;

  return (
    <RootContainer>
      <ErrorBoundary>
        <Modal
          open={isWizardOpen}
          width={modalWidth}
          wrapClassName={"arps-upload-wizard-modal"}
          onCancel={handleClose}
          closable={false}
          title={getModalTitle()}
          footer={
            <>
              {renderDuplicateMappingsWarning()}
              {renderFooter()}
            </>
          }>
          <ContentContainer>
            {detectProductsMutation.isLoading && renderLoadingState()}
            {loadingError && renderErrorState()}
            {dateWarnings && dateWarnings.length > 0 && renderDateWarning()}

            {hasDataToDisplay &&
              !detectProductsMutation.isLoading &&
              !dateWarnings?.length && (
                <ProductsContainer>
                  {isWellDataUpload ? (
                    <>
                      {renderWellDataMappingInstructions()}
                      {renderWellDataMappingSection()}
                    </>
                  ) : (
                    <>
                      {renderMappingInstructions()}
                      {renderProductMappingSection()}
                    </>
                  )}
                </ProductsContainer>
              )}
          </ContentContainer>
        </Modal>
      </ErrorBoundary>
    </RootContainer>
  );
};

export default ArpsUploadWizard;
